import { useState } from 'react'
import { getUser } from '../auth'
import { api } from '../api'
import { useToast } from '../components/Toast'
import { useAsyncData } from '../hooks/useAsyncData'
import { useColumnPrefs } from '../hooks/useColumnPrefs'
import RefreshButton from '../components/RefreshButton'
import HScroll from '../components/HScroll'
import Spinner, { PageSpinner } from '../components/Spinner'
import ColumnsMenu from '../components/ColumnsMenu'
import { Link } from 'react-router-dom'
import { ACCOUNT_TYPE_COLORS } from '../constants'
import Instant from '../components/Instant'

// Reserved (the resource name) and the Cancel action always show.
const TOGGLEABLE_COLUMNS = [
  { key: 'type', label: 'Type' },
  { key: 'reserved_by', label: 'Reserved by' },
  { key: 'created', label: 'Created' },
]

const DEFAULT_COLUMNS = {
  type: true,
  reserved_by: true,
  created: true,
}

function SectionHeader({ title, count }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <h2 className="text-base font-semibold text-base-content whitespace-nowrap">
        {title}
      </h2>
      {count != null && (
        <span className="text-xs font-mono font-medium text-base-content/50 bg-base-300/70 rounded-full px-2 py-0.5 leading-none">
          {count}
        </span>
      )}
      <div className="flex-1 h-px bg-base-300/70" />
    </div>
  )
}

function EmptyState({ children }) {
  return (
    <div className="rounded-box border border-base-300/60 py-10 text-center text-sm text-base-content/30">
      {children}
    </div>
  )
}

function ReservationTable({ rows, columns, showReservedBy, cancelling, onCancel, currentUserId }) {
  function reservableName(r) {
    if (!r.resource) return r.reservable_id.slice(0, 8) + '…'
    if (r.reservable_type === 'DEVICE') return r.resource.model ?? r.resource.mdi
    return r.resource.email
  }

  // The same address can be both a customer and a partner account, so "ACCOUNT"
  // alone no longer identifies the row — show which directory it is instead.
  function typeLabel(r) {
    if (r.reservable_type !== 'ACCOUNT') return r.reservable_type
    return r.resource?.account_type ?? r.reservable_type
  }

  function typeBadge(r) {
    if (r.reservable_type === 'DEVICE') return 'badge-secondary'
    return ACCOUNT_TYPE_COLORS[r.resource?.account_type] ?? 'badge-primary'
  }

  return (
    <HScroll className="rounded-box border border-base-300">
      <table className="table">
        <thead>
          <tr>
            {columns.type && <th>Type</th>}
            <th>Reserved</th>
            {showReservedBy && columns.reserved_by && <th>Reserved by</th>}
            {columns.created && <th>Created</th>}
            <th><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const isOwn = r.reserved_by === currentUserId
            const inBundle = !!r.bundle_id
            return (
              <tr key={r.id} className="hover">
                {columns.type && (
                  <td>
                    <div className="flex items-center gap-1.5">
                      <span className={`badge badge-sm ${typeBadge(r)}`}>
                        {typeLabel(r)}
                      </span>
                      {inBundle && (
                        <Link
                          to={`/bundles/${r.bundle_id}`}
                          className="badge badge-sm badge-outline hover:badge-primary"
                          title="View this bundle"
                        >
                          bundle
                        </Link>
                      )}
                    </div>
                  </td>
                )}
                <td className="font-medium">{reservableName(r)}</td>
                {showReservedBy && columns.reserved_by && (
                  <td>{r.reserved_by_email ?? r.reserved_by.slice(0, 8) + '…'}</td>
                )}
                {columns.created && (
                  <td><Instant iso={r.created_at} style="compact" /></td>
                )}
                <td>
                  {isOwn && (
                    inBundle ? (
                      <span className="text-xs text-base-content/40">Bundled</span>
                    ) : (
                      <button
                        className="btn btn-sm btn-error btn-outline"
                        disabled={cancelling === r.id}
                        aria-busy={cancelling === r.id}
                        onClick={() => onCancel(r)}
                      >
                        {cancelling === r.id ? <Spinner size="xs" /> : 'Cancel'}
                      </button>
                    )
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </HScroll>
  )
}

export default function Reservations() {
  const [currentUserId] = useState(() => getUser()?.id ?? null)
  const [cancelling, setCancelling] = useState(null)
  const { columns, toggle: toggleColumn, reset: resetColumns, isDefault: isDefaultColumns } =
    useColumnPrefs('reservations.visibleColumns', DEFAULT_COLUMNS)
  const { show } = useToast()

  const { data, setData, loading, reload } = useAsyncData(
    () => api.getReservations({ includeAll: true }),
    [],
    { onError: err => show(err.message, 'error') },
  )
  const reservations = data ?? []

  async function cancel(reservation) {
    setCancelling(reservation.id)
    try {
      await api.cancelReservation(reservation.id)
      show('Reservation cancelled', 'success')
      setData(rs => rs.filter(x => x.id !== reservation.id))
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setCancelling(null)
    }
  }

  const sorted = [...reservations].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  const mine = sorted.filter(r => r.reserved_by === currentUserId)
  const others = sorted.filter(r => r.reserved_by !== currentUserId)

  const tableProps = { columns, cancelling, onCancel: cancel, currentUserId }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <h1 className="text-2xl font-bold">Reservations</h1>
        <ColumnsMenu
          options={TOGGLEABLE_COLUMNS}
          columns={columns}
          onToggle={toggleColumn}
          onReset={resetColumns}
          isDefault={isDefaultColumns}
        />
        <RefreshButton onClick={reload} />
      </div>

      {loading ? (
        <PageSpinner />
      ) : (
        <div className="flex flex-col gap-10">
          <section>
            <SectionHeader title="My Reservations" count={mine.length} />
            {mine.length === 0 ? (
              <EmptyState>You have no active reservations</EmptyState>
            ) : (
              <ReservationTable {...tableProps} rows={mine} showReservedBy={false} />
            )}
          </section>

          <section>
            <SectionHeader title="Other Reservations" count={others.length} />
            {others.length === 0 ? (
              <EmptyState>No reservations from other users</EmptyState>
            ) : (
              <ReservationTable {...tableProps} rows={others} showReservedBy={true} />
            )}
          </section>
        </div>
      )}
    </div>
  )
}
